import React, { useEffect, useState } from "react";
import Card from "../../components/Card/Card";
import { toast } from "react-toastify";
import http from "../../http-common";
import { ButtonSet, Link, Row } from "carbon-components-react";
import { Column } from "carbon-components-react";
import { TextInput } from "carbon-components-react";
import { useForm } from "react-hook-form";
import { Form } from "carbon-components-react";
import { Button } from "carbon-components-react";
import { useNavigate, useParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  techEvalScoreSchema,
  adminEvalScoreSchema,
} from "../../services/SchemaService";

import CancelButton from "../../components/Buttons/CancelButton";
import { Undo24 } from "@carbon/icons-react";

const ScoreApplicationPage = () => {
  const params = useParams();
  const [uuid] = useState(params.uuid);
  const { user, isJury, isAdmin } = useAuth();
  const [criteria, setCriteria] = useState([]);
  const [evaluationType, setEvaluationType] = useState(null);
  const [existingScores, setExistingScores] = useState(null);
  const [isLoadingScores, setIsLoadingScores] = useState(false);

  // Determine evaluation type based on user role
  useEffect(() => {
    if (isJury) {
      setEvaluationType("TECHNICAL_EVALUATION");
    } else if (isAdmin) {
      setEvaluationType("ADMINISTRATIVE_EVALUATION");
    }
  }, [isJury, isAdmin]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      evaluationType === "TECHNICAL_EVALUATION"
        ? techEvalScoreSchema
        : adminEvalScoreSchema
    ),
    mode: "onBlur",
  });

  const navigate = useNavigate();

  // Helper function to transform code format from "TE101" to "1.1" or "AD101" to "1.1"
  const transformCode = (code) => {
    if (!code || code.length < 3) return code;

    // Remove first 2 characters (e.g., "TE" from "TE101" or "AD" from "AD101")
    const numericPart = code.substring(2);

    if (numericPart.length >= 2) {
      // For "101", we want "1.1" (replace the 0 with a dot)
      const result = numericPart.replace("0", ".");
      return result;
    }

    return numericPart;
  };

  const getCriteria = async () => {
    if (!evaluationType) return;

    try {
      const endpoint =
        evaluationType === "TECHNICAL_EVALUATION"
          ? "/applications/tech-evaluation-domains"
          : "/applications/admin-evaluation-domains";

      const response = await http.get(endpoint);
      setCriteria(response.data);
    } catch (error) {
      toast.error("Erro ao carregar mapa de pontuação");
    }
  };

  const getExistingScores = async () => {
    if (!evaluationType || !user?.username) return;

    try {
      setIsLoadingScores(true);
      const response = await http.get(
        `/applications/${uuid}/scores/${user.username}?evalType=${evaluationType}`
      );

      // console.log("Existing scores response:", response.data);

      // Check if there are existing scores
      if (
        response.data &&
        response.data.isScored &&
        response.data.criteriaScores
      ) {
        setExistingScores(response.data);

        // Transform criteriaScores array to form data format
        const formData = {};
        response.data.criteriaScores.forEach((criteria) => {
          formData[criteria.criteriaCode] = criteria.score;
          formData[criteria.criteriaCode + "Remarks"] = criteria.remarks || "";
        });

        // console.log("Form data to load:", formData);

        // Reset form with existing scores
        reset(formData);
        toast.info("Pontuação anterior carregada");
      } else {
        // console.log("No existing scores found");
        setExistingScores(null);
      }
    } catch (error) {
      // console.log("No existing scores found or error:", error);
      setExistingScores(null);
      // Don't show error toast for 404 - it's normal if no scores exist yet
      if (error.response?.status !== 404) {
        toast.error("Erro ao carregar pontuação anterior");
      }
    } finally {
      setIsLoadingScores(false);
    }
  };

  useEffect(() => {
    getCriteria();
    getExistingScores();
  }, [evaluationType, user]);

  const onSubmit = async (data) => {
    try {
      data.uuid = uuid;
      data.username = user.username;
      const response = await http.post(
        `/applications/score?evalType=${evaluationType}`,
        data
      );
      toast.success("Pontuação submetida com sucesso");

      // Navigate based on user role
      if (isJury) {
        navigate("../candidatures-by-jury/");
      } else if (isAdmin) {
        navigate("../candidatures-ranking/");
      } else {
        navigate("../avaliacao-de-candidaturas/");
      }
    } catch (error) {
      toast.error(error?.response?.data.message);
    }
  };

  // Show loading or error if evaluation type is not determined
  if (!evaluationType || isLoadingScores) {
    return (
      <Card title="Pontuar Candidatura">
        <p>Carregando...</p>
      </Card>
    );
  }

  return (
    <Card
      title={`Pontuar Candidatura - ${
        evaluationType === "TECHNICAL_EVALUATION"
          ? "Avaliação Técnica"
          : "Avaliação Administrativa"
      }${
        existingScores
          ? ` (Pontuação anterior: ${
              typeof existingScores.totalScore === "number"
                ? existingScores.totalScore.toFixed(2)
                : existingScores.totalScore
            } pts - ${existingScores.evaluationDate})`
          : ""
      }`}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        {criteria.map((d) => (
          <div className="mt-2 mb-3" key={d.domainName.code}>
            <h4 style={{ borderBottom: "1px solid #dedede" }}>
              {d.domainName.description}
            </h4>

            {d.criteria.map((c) => (
              <Row key={c.code}>
                <Column sm={12} md={12} lg={12}>
                  <p className="mt-1 mb-1">
                    <strong>
                      {transformCode(c.code)} - {c.criteria}
                    </strong>
                  </p>
                  <ul>
                    {c.description.split("|").map((item, index) => (
                      <li key={index} className="mb-1">
                        {item.trim()}
                      </li>
                    ))}
                  </ul>
                </Column>
                <Column sm={12} md={4} lg={4}>
                  <TextInput
                    id={c.code}
                    labelText=""
                    placeholder="0-20 pts"
                    {...register(c.code)}
                  />
                  <p className="field-error">{errors[c.code]?.message}</p>
                </Column>
                <Column sm={12} md={8} lg={8}>
                  <TextInput
                    id={c.code}
                    labelText=""
                    placeholder="Comentários"
                    maxLength="255"
                    {...register(c.code + "Remarks")}
                  />
                </Column>
              </Row>
            ))}
          </div>
        ))}
        <div className="mt-7">&nbsp;</div>
        <div className="form-buttons">
          <ButtonSet>
            <CancelButton icon={Undo24} label="Voltar" />
            <Button type="submit">Salvar</Button>
          </ButtonSet>
        </div>
      </Form>
    </Card>
  );
};

export default ScoreApplicationPage;
